import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { EventProvider } from "@/context/event-context";
import { AuthProvider } from "@/context/auth-context";
import { CartProvider } from "@/context/cart-context";
import { CartSummary } from "@/components/checkout/CartSummary";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminRoute } from "@/components/auth/AdminRoute";
import { ThemeProvider } from "@/components/variants/ThemeProvider";
import { useRouteTracking } from "@/hooks/use-analytics";

const RouteTracker = () => { useRouteTracking(); return null; };

// Landing — eager so first paint is fast
import Index from "./pages/Index";

// Public secondary — split out
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const MyTickets = lazy(() => import("./pages/MyTickets"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const EventInfo = lazy(() => import("./pages/EventInfo"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const TicketView = lazy(() => import("./pages/TicketView"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin — split into its own bundle so the landing page never ships it
const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminEvents = lazy(() => import("./pages/admin/Events"));
const AdminOrders = lazy(() => import("./pages/admin/Orders"));
const AdminAttendees = lazy(() => import("./pages/admin/Attendees"));
const AdminBottles = lazy(() => import("./pages/admin/Bottles"));
const AdminScanner = lazy(() => import("./pages/admin/Scanner"));
const AdminPromoCodes = lazy(() => import("./pages/admin/PromoCodes"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminTables = lazy(() => import("./pages/admin/Tables"));
const AdminCreateTables = lazy(() => import("./pages/admin/CreateTables"));
const AdminWalkUp = lazy(() => import("./pages/admin/WalkUp"));
const AdminSendTickets = lazy(() => import("./pages/admin/SendTickets"));
const AdminTimeline = lazy(() => import("./pages/admin/Timeline"));
const AdminGallery = lazy(() => import("./pages/admin/GalleryAdmin"));
const AdminRunners = lazy(() => import("./pages/admin/Runners"));
const AdminTablePackages = lazy(() => import("./pages/admin/TablePackages"));
const AdminAudit = lazy(() => import("./pages/admin/Audit"));

const RouteFallback = () => (
  <div className="min-h-screen bg-cream" aria-busy="true" aria-label="Loading" />
);

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <EventProvider>
          <CartProvider>
            <Toaster />
            <Sonner />
            <ThemeProvider>
            <BrowserRouter>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  {/* Public */}
                  <Route path="/" element={<Index />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />

                  {/* Checkout (public — guest checkout supported) */}
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/order-confirmation/:id" element={<OrderConfirmation />} />
                  <Route path="/event-info" element={<EventInfo />} />
                  <Route path="/gallery" element={<GalleryPage />} />
                  <Route path="/ticket/:ticketId/:shareToken" element={<TicketView />} />

                  {/* Customer (protected) */}
                  <Route path="/my-tickets" element={<ProtectedRoute><MyTickets /></ProtectedRoute>} />
                  <Route path="/my-orders" element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />

                  {/* Admin (admin-only) */}
                  <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                    <Route index element={<Dashboard />} />
                    <Route path="events" element={<AdminEvents />} />
                    <Route path="orders" element={<AdminOrders />} />
                    <Route path="attendees" element={<AdminAttendees />} />
                    <Route path="bottles" element={<AdminBottles />} />
                    <Route path="promos" element={<AdminPromoCodes />} />
                    <Route path="scan" element={<AdminScanner />} />
                    <Route path="tables" element={<AdminTables />} />
                    <Route path="tables/create" element={<AdminCreateTables />} />
                    <Route path="table-packages" element={<AdminTablePackages />} />
                    <Route path="walk-up" element={<AdminWalkUp />} />
                    <Route path="send-tickets" element={<AdminSendTickets />} />
                    <Route path="timeline" element={<AdminTimeline />} />
                    <Route path="gallery" element={<AdminGallery />} />
                    <Route path="runners" element={<AdminRunners />} />
                    <Route path="audit" element={<AdminAudit />} />
                    <Route path="settings" element={<AdminSettings />} />
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <CartSummary />
            </BrowserRouter>
            </ThemeProvider>
          </CartProvider>
        </EventProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
